/**
 * [TD-47.6c E1-CORRECTION] PGFAnalyticsTab - Tab 2 of the Zone 2
 * PlayerAnalysisCanvas. This is the paid-tier surface.
 *
 * Layout (founder-locked E1 correction):
 *   • Top strip - PGF Rank + PGF Value + Default Positional Ranking
 *   • Middle    - Radar (5-axis, compact) on the left, position
 *                 gauges on the right
 *   • Gold/Silver alert ribbon
 *   • Weekly Matchup Bar (Weeks 1-18)
 *   • PGF Analysis paragraph (LLM fact-packet output)
 *   • Collapsible deep-dive for secondary stats
 *
 * For unpaid users (no `pgf_analytics_alpha` flag + role !== admin), the
 * entire body renders inside a blur+CTA paywall overlay so users see
 * that advanced analytics exist without being able to read the data.
 *
 * Owns its own canvas+analysis fetch - pulls /analytics/players/{id}/canvas
 * and /draft/{league}/player-analysis lazily on player change.
 *
 * Per A.7 Narrative Discipline Guard: paragraph eyebrow reads "PGF Analysis";
 * disallowed phrases never appear in code or copy.
 */
import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { authHeaders, useAuth } from "../../contexts/AuthContext";
import { useEntitlements } from "../../hooks/useEntitlements";
import { PGFRadarChart } from "./PGFRadarChart";
import { PGFEdgeBadge } from "./PGFEdgeBadge";
import { PaywallOverlay } from "./PaywallOverlay";
import KDEFMatchupCard from "./KDEFMatchupCard";

// [Session C-A CP-2 (P0b)] Feature flag: player-comparison flow is deferred
// until C-B or later. Set to true when comparison is implemented.
const FEATURE_COMPARE_ENABLED = false;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_DISPLAY = {
  elite: "Elite",
  top_tier: "Top-Tier",
  solid: "Solid",
  roster_worthy: "Roster-Worthy",
  bench: "Bench",
};

export default function PGFAnalyticsTab({
  leagueId,
  draftId,
  currentPlayer,
  basicMetrics,
  metricsLoading,
  onUpgrade,
}) {
  const { user } = useAuth();
  const { hasDraftAnalytics } = useEntitlements();
  const isPaid =
    user?.role === "admin" || !!user?.alpha_flags?.pgf_analytics_alpha || hasDraftAnalytics;

  const [canvas, setCanvas] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deepDiveOpen, setDeepDiveOpen] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  const playerId = currentPlayer?.id;

  // Canvas fetch - required for all of the PGF analytics surfaces.
  useEffect(() => {
    if (!playerId || !leagueId) {
      setCanvas(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(
          `${API}/analytics/players/${playerId}/canvas`,
          {
            params: { season_year: 2024, scoring_format: "half_ppr", league_id: leagueId },
            headers: authHeaders(),
          },
        );
        if (!cancelled) setCanvas(r.data);
      } catch {
        if (!cancelled) setCanvas(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [playerId, leagueId]);

  // Scouting paragraph - POSTs to the LLM fact-packet endpoint (R2-cached server-side).
  useEffect(() => {
    if (!isPaid || !playerId || !leagueId) {
      setAnalysis(null);
      return;
    }
    let cancelled = false;
    setAnalysisError(null);
    (async () => {
      try {
        const r = await axios.post(
          `${API}/draft/${leagueId}/player-analysis`,
          { player_id: playerId },
          { headers: authHeaders() },
        );
        if (!cancelled) setAnalysis(r.data);
      } catch (e) {
        if (!cancelled) {
          setAnalysis(null);
          setAnalysisError(e?.response?.data?.detail || "Analysis unavailable");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isPaid, playerId, leagueId]);

  if (!currentPlayer) {
    return (
      <div
        className="h-full flex flex-col items-center justify-center text-center px-4"
        data-testid="pgf-analytics-empty"
      >
        <p className="font-display font-bold text-sm uppercase tracking-wider text-[#A0A0A0]">
          Click any player
        </p>
        <p className="font-body text-xs text-[#666] mt-2 max-w-xs">
          PGF Analytics appears here when you tap a row in the pool.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center" data-testid="pgf-analytics-loading">
        <div className="w-6 h-6 border-2 border-[#FF6B00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!canvas) {
    return (
      <div
        className="h-full flex items-center justify-center px-4"
        data-testid="pgf-analytics-unavailable"
      >
        <p className="font-body text-xs text-[#A0A0A0] text-center">
          PGF analytics not available for this player (K/DEF/IDP, or no
          scored row yet).
        </p>
      </div>
    );
  }

  const rank = canvas.pgf_rank || {};
  const axes = canvas.axes || {};
  const weekly = canvas.weekly_matchup_vector;
  const byeWeek = canvas.player?.bye_week;
  const adpRank = analysis?.adp ? analysis.adp.toFixed(1) : null;
  const raw = canvas.raw_metrics;
  const position = canvas.player?.position;
  const nflTeam = canvas.player?.nfl_team;
  const photoUrl = currentPlayer?.photo_url || canvas.player?.photo_url;
  // [E1-CORRECTION Fix 10] Basic stats sourced from /players/{id}/metrics
  // - surfaced alongside PGF analytics so paid users see one comprehensive
  // view without tab-switching.
  const pmRow = basicMetrics?.metrics;
  const pmFormat = basicMetrics?.scoring_format_used || "half_ppr";
  const ppgKey = `pts_per_game_${pmFormat}`;
  const ppgValue = pmRow?.[ppgKey] ?? raw?.ppg;
  const adpValue = pmRow?.avg_adp ?? analysis?.adp;

  return (
    <div className="relative h-full overflow-hidden" data-testid="pgf-analytics-tab">
      <div
        className={`h-full p-1 flex flex-col gap-1 overflow-y-auto ${isPaid ? "" : "pointer-events-none select-none"}`}
        data-testid="pgf-analytics-canvas-layout"
      >
        {/* ── TOP ZONE - Tall portrait image | Centered name stack | Right column ── */}
        <div
          data-testid="pgf-analytics-player-header"
          className="shrink-0 flex items-stretch gap-3 px-1"
          style={{ height: 150 }}
        >
          {/* Tall portrait image - ~2.5× v1.0 size per redline #1.
              [v1.6] Wrapped in TiltCard for premium 3D trading-card effect. */}
          <TiltCard>
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={currentPlayer.name || "Player"}
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="rounded-md object-cover border border-[#333] shrink-0"
                style={{ width: 130, height: 150 }}
                data-testid="pgf-analytics-player-image"
              />
            ) : (
              <div
                className="rounded-md bg-[#1A1A1A] border border-[#333] shrink-0"
                style={{ width: 130, height: 150 }}
                data-testid="pgf-analytics-player-image"
              />
            )}
          </TiltCard>
          {/* Centered name stack - Name / POS / TEAM / Bye */}
          <div className="flex-1 min-w-0 flex flex-col items-center justify-center text-center">
            <p
              className="font-display font-black uppercase text-[#F5F5F5] leading-[1.05] truncate w-full"
              style={{ fontSize: "clamp(20px, 2.2vw, 32px)" }}
              data-testid="pgf-analytics-player-name"
            >
              {currentPlayer.name}
            </p>
            <p
              className="font-display font-black uppercase mt-0.5 text-[#FF6B00] leading-tight"
              style={{ fontSize: "clamp(12px, 1.1vw, 16px)" }}
            >
              {(position || "").toUpperCase()}
            </p>
            <p className="font-display font-bold uppercase text-[#F5F5F5] leading-tight text-xs">
              {nflTeam || "FA"}
            </p>
            {byeWeek != null && (
              <p className="font-body text-[11px] text-[#A0A0A0]">
                Bye Wk {byeWeek}
              </p>
            )}
          </div>
          {/* Right column - Higher Ranked + Compare stacked + centered */}
          <div className="shrink-0 flex flex-col items-end justify-center gap-2" style={{ width: 170 }}>
            <HigherRankedPill adpValue={adpValue} rank={rank} />
            {/* [Edge Surfaces §2a] PGF Edge badge - sits under the rank
                pill so drafters see market divergence at a glance. */}
            {canvas.pgf_edge && <PGFEdgeBadge edge={canvas.pgf_edge} size="sm" />}
            {FEATURE_COMPARE_ENABLED && <CompareButton playerName={currentPlayer.name} />}
          </div>
        </div>

        {/* [Session C-A CP-2 §3.9] K/DEF get a simplified matchup card
            instead of the five-axis radar. Matchup score + implied opp
            total remain "data pending" until CP-6 wires position_matchup_context. */}
        {(String(position || "").toUpperCase() === "K" ||
          String(position || "").toUpperCase() === "DEF") ? (
          <div className="shrink-0 flex justify-center">
            <div className="w-[70%] max-w-[420px]">
              <KDEFMatchupCard
                position={position}
                team={nflTeam}
                byeWeek={byeWeek}
                availability={currentPlayer.status || raw?.injury_status || "Active"}
                adpRank={adpValue || null}
                matchupScore={raw?.matchup_score ?? null}
                impliedOpponentTotal={raw?.implied_opponent_total ?? null}
              />
            </div>
          </div>
        ) : (
        <>
        {/* ── AVAILABILITY - sits ABOVE the radar zone (own row, no overlap) ── */}
        <div className="shrink-0 flex justify-center">
          <div className="w-[28%]">
            <AxisBlock
              label="Availability"
              testid="pgf-axis-availability"
              score={axes.availability}
              bullets={availabilityBullets(raw, position)}
              compact
              animationKey={playerId}
            />
          </div>
        </div>

        {/* ── MIDDLE ZONE - Radar centerpiece with 4 axis blocks at pentagon vertices ── */}
        <div
          data-testid="pgf-analytics-middle"
          className="shrink-0 relative"
          style={{ minHeight: 320 }}
        >
          {/* Radar - fills the full middle zone */}
          <div
            data-testid="pgf-radar-no-scale-numbers"
            className="absolute inset-0 flex items-center justify-center text-[#111111] dark:text-[#F5F5F5]"
          >
            <PGFRadarChart axes={axes} />
          </div>

          {/* [v1.4 Redline #1] Stability - top-left vertex, equidistant from center */}
          <div className="absolute top-[18%] left-[6%] w-[22%] z-10">
            <AxisBlock
              label="Stability"
              testid="pgf-axis-stability"
              score={axes.stability}
              bullets={stabilityBullets(raw, position)}
              compact
              animationKey={playerId}
            />
          </div>

          {/* [v1.4 Redline #1] Productivity - top-right vertex */}
          <div className="absolute top-[18%] right-[6%] w-[22%] z-10">
            <AxisBlock
              label="Productivity"
              testid="pgf-axis-productivity"
              score={axes.productivity}
              bullets={productivityBullets(raw, position)}
              compact
              animationKey={playerId}
            />
          </div>

          {/* [v1.4 Redline #1] Match Up - bottom-left vertex (mirror of Stability) */}
          <div className="absolute top-[60%] left-[6%] w-[22%] z-10">
            <AxisBlock
              label="Match Up"
              testid="pgf-axis-matchup"
              score={axes.matchup}
              bullets={matchupBullets(raw, position)}
              compact
              animationKey={playerId}
            />
          </div>

          {/* [v1.4 Redline #1] Opportunity - bottom-right vertex (mirror of Productivity) */}
          <div className="absolute top-[60%] right-[6%] w-[22%] z-10">
            <AxisBlock
              label="Opportunity"
              testid="pgf-axis-opportunity"
              score={axes.opportunity}
              bullets={opportunityBullets(raw, position)}
              compact
              animationKey={playerId}
            />
          </div>
        </div>
        </>
        )}

        {/* ── NEWS STRIP - centered 5 cards, chevrons flank the strip ── */}
        <NewsStrip playerName={currentPlayer.name} />

        {/* ── BOTTOM ROW - Tier peers (ranked) | Strength of Schedule (Wk labels) ── */}
        <div className="shrink-0 grid grid-cols-2 gap-1" style={{ height: 84 }}>
          <TierPeerBars
            currentName={currentPlayer.name}
            currentScore={ppgValue}
            position={position}
            playerId={currentPlayer.id || currentPlayer.player_id}
            leagueId={leagueId}
          />
          <StrengthOfSchedule
            byeWeek={byeWeek}
            weeklyVector={weekly}
            playerId={currentPlayer.id || currentPlayer.player_id}
          />
        </div>
      </div>

      {!isPaid && <PaywallOverlay onUpgrade={onUpgrade} mode="draft" draftId={draftId || leagueId} />}
    </div>
  );
}

// ── Helper components (Phase 2d Session B - auction draft UI redesign) ──

function HigherRankedPill({ adpValue, rank }) {
  // Wired to real positional_rank vs adp_rank diff (verdict Redline D item 1).
  // Show pill when a player's positional rank is worse than their ADP rank
  // by ≥ 6 slots - implies a higher-ranked option may still be available.
  const score = Number(rank?.score) || 0;
  const adp = Number(adpValue) || 0;
  const showPill = score > 0 && adp > 0 && adp + 6 < score;
  if (!showPill) return null;
  return (
    <span
      data-testid="pgf-higher-ranked-pill"
      className="font-display font-black text-[9px] uppercase tracking-widest text-amber-300 bg-amber-300/10 border border-amber-300/40 rounded-sm px-1.5 py-0.5"
    >
      Higher Ranked Available
    </span>
  );
}

function CompareButton({ playerName }) {
  // [Session C placeholder] Comparison modal lands with analytics data wiring.
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <button
        type="button"
        data-testid="pgf-compare-btn"
        onClick={() => setOpen(true)}
        className="font-display font-black text-[10px] uppercase tracking-wider text-[#FF6B00] border border-[#FF6B00]/40 hover:bg-[#FF6B00]/10 rounded-sm px-2 py-0.5 transition-colors"
      >
        Click to Compare
      </button>
      {open && (
        <div
          data-testid="pgf-compare-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
          onClick={() => setOpen(false)}
        >
          <div className="rounded-lg bg-[#1C1C1C] border border-[#2E2E2E] p-6 max-w-md text-center">
            <p className="font-display font-black text-base uppercase text-[#FF6B00] mb-2">
              Player Comparison
            </p>
            <p className="font-body text-sm text-[#F5F5F5]">
              Compare <strong>{playerName}</strong> against the next-best available player.
            </p>
            <p className="font-body text-xs text-[#A0A0A0] mt-2 italic">
              Coming with Analytics Data Wiring (Session C).
            </p>
            <button
              onClick={() => setOpen(false)}
              className="mt-4 px-4 py-1.5 bg-[#FF6B00] text-white font-display font-black text-xs uppercase tracking-wider rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// Production / Opportunity / Stability / Match Up / Availability axis block.
// [v1.2 Redline #1a] Border color follows the score's slider position
// (red 0-33 / yellow 33-66 / green 66-100) - same hue as the marker, so
// the eye reads the urgency without category lookup.
function _scoreColor(pct) {
  if (pct >= 66) return "#00C853";
  if (pct >= 33) return "#FFD700";
  return "#FF4444";
}

function AxisBlock({ label, score, bullets, testid, compact = false, animationKey }) {
  const pct = Math.max(0, Math.min(100, Math.round((Number(score) || 0) * 100)));
  const borderColor = _scoreColor(pct);
  return (
    <div
      data-testid={testid}
      className={`flex flex-col rounded-md bg-[#0F0F12] border-2 min-h-0 overflow-hidden ${compact ? "px-2 py-1 gap-0.5" : "px-2 py-1.5 gap-1"}`}
      style={{ borderColor }}
    >
      <div className="flex items-baseline justify-between">
        <p
          className="font-display font-black uppercase tracking-wider text-[#F5F5F5]"
          style={{ fontSize: compact ? 13 : 15 }}
          data-testid={`${testid}-label`}
        >
          {label}
        </p>
        <p
          className="font-display font-black text-[#F5F5F5]"
          style={{ fontSize: compact ? 16 : 18 }}
          data-testid={`${testid}-score`}
        >
          {pct}
        </p>
      </div>
      <div className="relative h-2 rounded-sm overflow-hidden bg-[#1A1A1A] shrink-0">
        <div className="absolute inset-0 flex">
          <div className="flex-1" style={{ background: "linear-gradient(90deg,#FF4444,#FFD700)" }} />
          <div className="flex-1" style={{ background: "linear-gradient(90deg,#FFD700,#00C853)" }} />
        </div>
        {/* [v1.6] Animated marker - sweeps from 0% to pct% over 1.2s with a
            back-out overshoot + intensifying glow, then a single pulse at
            the endpoint. Re-plays every player swap via `animationKey`. */}
        <motion.div
          key={animationKey}
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-0.5 h-3 bg-white"
          initial={{ left: "0%", boxShadow: "0 0 0 rgba(255,255,255,0)", scale: 1 }}
          animate={{
            left: `${pct}%`,
            boxShadow: [
              "0 0 4px rgba(255,255,255,0.7)",
              "0 0 14px rgba(255,255,255,1)",
              "0 0 8px rgba(255,255,255,0.9)",
            ],
            scale: [1, 1, 1.35, 1],
          }}
          transition={{
            duration: 1.2,
            ease: [0.34, 1.56, 0.64, 1],
            times: [0, 0.72, 0.86, 1],
          }}
          data-testid={`${testid}-marker`}
          aria-label={`${label} score ${pct}`}
        />
      </div>
      <ul className="font-body text-[10px] text-[#A0A0A0] leading-snug list-none m-0 p-0 mt-0.5 overflow-hidden">
        {bullets.slice(0, compact ? 2 : 3).map((b, i) => (
          <li key={i} className="truncate" data-testid={`${testid}-bullet-${i}`}>· {b}</li>
        ))}
      </ul>
    </div>
  );
}

// [v1.6] TiltCard - trading-card 3D tilt effect on mouse move. Adds a
// specular sheen via a linear-gradient overlay that follows the cursor.
// Respects prefers-reduced-motion.
function TiltCard({ children }) {
  const ref = useRef(null);
  const [transform, setTransform] = useState("");
  const [sheen, setSheen] = useState({ x: 50, y: 50, opacity: 0 });

  const handleMove = (e) => {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTransform(`perspective(900px) rotateY(${x * 18}deg) rotateX(${-y * 18}deg) scale(1.03)`);
    setSheen({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
      opacity: 0.35,
    });
  };
  const handleLeave = () => {
    setTransform("");
    setSheen((s) => ({ ...s, opacity: 0 }));
  };

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className="relative shrink-0"
      style={{
        transform,
        transition: "transform 180ms cubic-bezier(0.22, 0.61, 0.36, 1)",
        transformStyle: "preserve-3d",
        willChange: "transform",
      }}
      data-testid="pgf-analytics-tilt-card"
    >
      {children}
      {/* Specular highlight - tracks cursor for that "foil card" gloss */}
      <div
        aria-hidden="true"
        className="absolute inset-0 rounded-md pointer-events-none"
        style={{
          background: `radial-gradient(circle at ${sheen.x}% ${sheen.y}%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 45%)`,
          opacity: sheen.opacity,
          transition: "opacity 200ms ease-out",
          mixBlendMode: "screen",
        }}
      />
    </div>
  );
}

// News strip - real feed lands in C-B P10 (X/beat writer pipeline). Until
// then, honest empty state per CP-1 P0a (Session C-A lock, Redline D item 3).
function NewsStrip({ playerName: _playerName }) {
  const scrollRef = React.useRef(null);
  const cards = React.useMemo(() => ([]), []);
  const hasCards = cards.length > 0;
  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 200, behavior: "smooth" });
  };
  return (
    <div className="shrink-0" data-testid="pgf-news-strip">
      {/* [v1.2 Redline #6] News title centered over the tile row. */}
      <p className="font-display font-black text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0] text-center">
        News
      </p>
      <div className="flex items-stretch gap-1 h-[92px]">
        {hasCards && (
          <button
            type="button"
            data-testid="pgf-news-chevron-left"
            aria-label="Scroll news left"
            onClick={() => scroll(-1)}
            className="shrink-0 w-7 rounded bg-[#1C1C1C] border border-[#2E2E2E] hover:bg-[#252525] flex items-center justify-center text-[#F5F5F5] text-base"
          >
            ‹
          </button>
        )}
        <div
          ref={scrollRef}
          className="flex-1 flex gap-2 overflow-x-auto justify-center snap-x snap-mandatory"
          style={{ scrollbarWidth: "none" }}
        >
          {hasCards ? cards.map((c, i) => (
            <a
              key={i}
              href="#"
              onClick={(e) => e.preventDefault()}
              data-testid={`pgf-news-card-${i}`}
              className="shrink-0 snap-start w-[190px] h-full flex gap-2 rounded-md bg-[#0F0F12] border-2 hover:bg-[#141418] p-1.5 transition-colors"
              style={{ borderColor: c.color }}
            >
              <div className="w-14 h-full rounded-sm bg-[#1A1A1A] border border-[#252525] shrink-0" />
              <div className="flex-1 min-w-0 flex flex-col justify-between overflow-hidden">
                <p
                  className="font-display font-black text-[9px] uppercase tracking-widest truncate"
                  style={{ color: c.color }}
                >
                  {c.source}
                </p>
                <p className="font-body text-[10px] text-[#F5F5F5] leading-tight">
                  {c.headline}
                </p>
              </div>
            </a>
          )) : (
            <div
              data-testid="pgf-news-empty-state"
              className="flex-1 flex items-center justify-center rounded-md bg-[#0F0F12] border border-[#2E2E2E]"
            >
              <p className="font-body text-[11px] text-[#666] italic">
                No recent PGF intel
              </p>
            </div>
          )}
        </div>
        {hasCards && (
          <button
            type="button"
            data-testid="pgf-news-chevron-right"
            aria-label="Scroll news right"
            onClick={() => scroll(1)}
            className="shrink-0 w-7 rounded bg-[#1C1C1C] border border-[#2E2E2E] hover:bg-[#252525] flex items-center justify-center text-[#F5F5F5] text-base"
          >
            ›
          </button>
        )}
      </div>
    </div>
  );
}

// [Session C-A CP-2 (P0b)] Tier peers - fetched from
// GET /api/analytics/players/{playerId}/tier-peers. Empty state renders
// honestly per Redline D. Mock pool from Session C-A CP-1 has been retired.
function TierPeerBars({ currentName, currentScore, position, playerId, leagueId }) {
  const [peers, setPeers] = React.useState(null); // null = loading, [] = empty
  const [error, setError] = React.useState(false);
  const { authToken } = useAuth();

  React.useEffect(() => {
    if (!playerId) {
      setPeers([]);
      return;
    }
    let cancelled = false;
    const params = { limit: 5 };
    if (leagueId) params.league_id = leagueId;
    axios
      .get(`${API}/analytics/players/${playerId}/tier-peers`, {
        headers: authHeaders(authToken),
        params,
      })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res.data?.peers) ? res.data.peers : [];
        setPeers(rows);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setPeers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [playerId, leagueId, authToken]);

  const emptyState = (
    <div
      data-testid="pgf-tier-peers"
      className="rounded-md bg-[#0F0F12] border border-[#2E2E2E] px-2 py-1 flex flex-col overflow-hidden"
    >
      <p className="font-display font-black text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0] shrink-0">
        Tier peers · {(position || "").toUpperCase()}
      </p>
      <div
        className="flex-1 flex items-center justify-center"
        data-testid="pgf-tier-peers-empty-state"
      >
        <p className="font-body text-[11px] text-[#666] italic">
          Tier peers update with PGF Analytics
        </p>
      </div>
    </div>
  );

  if (peers === null && !error) return emptyState; // loading - same shell
  if (!peers || peers.length === 0) return emptyState;

  // Build the bar chart from real peers + the current player.
  const safe = Number(currentScore) || 12;
  const lastName = (currentName || "").split(" ").slice(-1)[0] || "You";
  const bars = peers.map((p) => ({
    name: (p.full_name || "").split(" ").slice(-1)[0] || "?",
    score: Number(p.pgf_rank_score) || 0,
    current: false,
    isDrafted: Boolean(p.is_drafted),
    photoUrl: p.photo_url || null,
  }));
  bars.push({ name: lastName, score: safe, current: true, isDrafted: false, photoUrl: null });
  bars.sort((a, b) => b.score - a.score);
  const maxScore = Math.max(...bars.map((b) => b.score)) || 1;

  return (
    <div
      data-testid="pgf-tier-peers"
      className="rounded-md bg-[#0F0F12] border border-[#2E2E2E] px-2 py-1 flex flex-col overflow-hidden"
    >
      <p className="font-display font-black text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0] shrink-0">
        Tier peers · {(position || "").toUpperCase()}
      </p>
      <div className="flex-1 flex items-end justify-between gap-1 mt-0.5 min-h-0">
        {bars.map((p, i) => {
          const h = Math.max(10, Math.round((p.score / maxScore) * 100));
          const barColor = p.current
            ? "#FF6B00"
            : p.isDrafted
            ? "#4B4B4B"
            : "#2E2E2E";
          return (
            <div key={`${p.name}-${i}`} className="flex-1 flex flex-col items-center min-w-0">
              {p.photoUrl ? (
                <img
                  src={p.photoUrl}
                  alt={p.name}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  className="rounded-full border shrink-0 mb-0.5"
                  style={{
                    width: 18,
                    height: 18,
                    borderColor: p.current ? "#FF6B00" : "#2E2E2E",
                    opacity: p.isDrafted ? 0.5 : 1,
                    objectFit: "cover",
                  }}
                  data-testid={`pgf-tier-peer-photo-${i}`}
                />
              ) : null}
              <div
                className="w-full rounded-sm"
                style={{
                  height: `${h}%`,
                  background: barColor,
                  opacity: p.isDrafted ? 0.5 : 1,
                }}
                data-testid={`pgf-tier-peer-bar-${i}`}
              />
              <p
                className={`font-display font-black text-[9px] uppercase truncate w-full text-center mt-0.5 ${
                  p.current
                    ? "text-[#FF6B00]"
                    : p.isDrafted
                    ? "text-[#666] line-through"
                    : "text-[#A0A0A0]"
                }`}
                title={p.name}
                data-testid={`pgf-tier-peer-name-${i}`}
              >
                {p.name}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Strength of Schedule - cells aligned in one row, Wk# labels in a
// second row, Bye text in a third row (only visible under the bye cell)
// so the cell positions stay flush across the strip. (Redline #5)
function StrengthOfSchedule({ byeWeek, weeklyVector, playerId }) {
  const cells = React.useMemo(() => {
    const out = [];
    const seed = (playerId || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    for (let wk = 1; wk <= 17; wk++) {
      if (wk === byeWeek) {
        out.push({ wk, color: "#000000", bye: true });
        continue;
      }
      const v = weeklyVector?.[String(wk)];
      let pct;
      if (typeof v === "number") pct = Math.max(0, Math.min(1, v));
      else pct = ((seed + wk * 37) % 100) / 100;
      const color = pct >= 0.66 ? "#00C853" : pct >= 0.33 ? "#FFD700" : "#FF4444";
      out.push({ wk, color, bye: false });
    }
    return out;
  }, [byeWeek, weeklyVector, playerId]);
  return (
    <div
      data-testid="pgf-strength-of-schedule"
      className="rounded-md bg-[#0F0F12] border border-[#2E2E2E] px-2 py-1 flex flex-col overflow-hidden"
    >
      <p className="font-display font-black text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0] shrink-0">
        Strength of schedule
      </p>
      {/* Row 1: colored cells - uniform Y baseline */}
      <div className="flex gap-[2px] mt-1">
        {cells.map((c) => (
          <div
            key={`bar-${c.wk}`}
            data-testid={`pgf-sos-cell-${c.wk}`}
            className="flex-1 h-4 rounded-[2px]"
            style={{ background: c.color }}
            title={c.bye ? `Week ${c.wk} · Bye` : `Week ${c.wk}`}
          />
        ))}
      </div>
      {/* Row 2: Wk# labels - uniform Y baseline */}
      <div className="flex gap-[2px] mt-0.5">
        {cells.map((c) => (
          <span
            key={`wk-${c.wk}`}
            className={`flex-1 font-display font-black text-[8px] text-center leading-tight truncate ${c.bye ? "text-[#F5F5F5]" : "text-[#A0A0A0]"}`}
            data-testid={`pgf-sos-label-${c.wk}`}
          >
            Wk{c.wk}
          </span>
        ))}
      </div>
      {/* Row 3: Bye text - only the bye cell renders content; the rest
          render an empty span so the row keeps its Y reserved without
          shifting any column. */}
      <div className="flex gap-[2px] leading-tight">
        {cells.map((c) => (
          <span
            key={`by-${c.wk}`}
            className="flex-1 font-display font-black text-[8px] text-center text-[#FF6B00]"
            data-testid={c.bye ? `pgf-sos-bye-${c.wk}` : undefined}
          >
            {c.bye ? "Bye" : "\u00A0"}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Bullet helpers - plain-English insights per axis from raw stats ──
function productivityBullets(raw, position) {
  if (!raw) return ["Awaiting last-season production data."];
  const out = [];
  if (raw.ppg != null) out.push(`${Number(raw.ppg).toFixed(1)} PPG last season`);
  if (raw.target_share != null && (position === "WR" || position === "TE")) {
    out.push(`${(raw.target_share * 100).toFixed(0)}% target share`);
  }
  if (raw.rush_attempts_per_game != null && position === "RB") {
    out.push(`${Number(raw.rush_attempts_per_game).toFixed(1)} rush att/game`);
  }
  if (raw.red_zone_share != null) {
    out.push(`${(raw.red_zone_share * 100).toFixed(0)}% red-zone share`);
  }
  while (out.length < 3) out.push("Trend data pending");
  return out;
}

function opportunityBullets(raw, position) {
  if (!raw) return ["Awaiting opportunity inputs."];
  const out = [];
  if (raw.depth_chart_rank != null) out.push(`Depth chart rank: ${raw.depth_chart_rank}`);
  if (raw.snap_count_pct != null) out.push(`${(raw.snap_count_pct * 100).toFixed(0)}% snap share`);
  if (raw.offense_pace_rank != null) out.push(`Team pace rank: ${raw.offense_pace_rank}`);
  while (out.length < 3) out.push("Depth chart data pending");
  return out;
}

function availabilityBullets(raw /* , position */) {
  const out = [];
  if (raw?.games_played != null) out.push(`${raw.games_played} games last season`);
  if (raw?.injury_status) out.push(`Injury status: ${raw.injury_status}`);
  if (raw?.snap_count_pct != null) out.push(`${(raw.snap_count_pct * 100).toFixed(0)}% snap share when active`);
  while (out.length < 2) out.push("Health history data pending");
  return out;
}

function stabilityBullets(raw, position) {
  const out = [];
  if (raw?.boom_rate != null) out.push(`${(raw.boom_rate * 100).toFixed(0)}% boom rate`);
  if (raw?.bust_rate != null) out.push(`${(raw.bust_rate * 100).toFixed(0)}% bust rate`);
  if (raw?.weekly_variance != null) out.push(`Weekly variance: ${Number(raw.weekly_variance).toFixed(1)}`);
  if (position && out.length < 3) out.push(`${(position || "").toUpperCase()} tier stability rated`);
  while (out.length < 3) out.push("Variance trend data pending");
  return out;
}

function matchupBullets(raw /* , position */) {
  const out = [];
  if (raw?.schedule_strength_vs_position != null) {
    out.push(`SoS vs position: ${(raw.schedule_strength_vs_position * 100).toFixed(0)}%`);
  }
  if (raw?.next_opponent_rank != null) out.push(`Next opp rank: ${raw.next_opponent_rank}`);
  if (raw?.home_away_split != null) out.push(`Home/Away split: ${raw.home_away_split}`);
  while (out.length < 3) out.push("Matchup data pending");
  return out;
}

